import React, { useState, useEffect } from 'react';
import { Star, MessageSquare, User, Clock, RefreshCw } from 'lucide-react';
import { useLanguage } from '../LanguageContext';
import { useSiteStats } from '../SiteStatsContext';

const FeedbacksPage = () => {
  const { t } = useLanguage();
  const stats = useSiteStats();
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Function to convert Discord emoji format to displayable content
  const parseDiscordContent = (content) => {
    if (!content) return '';
    
    // Replace animated emojis <a:name:id> with image
    let parsed = content.replace(/<a:(\w+):(\d+)>/g, (match, name, id) => {
      return `<img src="https://cdn.discordapp.com/emojis/${id}.gif" alt="${name}" class="inline-block w-5 h-5 align-middle" />`;
    });
    
    // Replace static emojis <:name:id> with image
    parsed = parsed.replace(/<:(\w+):(\d+)>/g, (match, name, id) => {
      return `<img src="https://cdn.discordapp.com/emojis/${id}.png" alt="${name}" class="inline-block w-5 h-5 align-middle" />`;
    });
    
    return parsed;
  };

  const fetchReviews = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_URL}/api/discord/reviews`);
      if (response.ok) {
        const data = await response.json();
        setReviews(data);
      } else {
        setError('Erreur lors du chargement des avis');
      }
    } catch (err) {
      console.error('Error fetching reviews:', err);
      setError('Impossible de charger les avis');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
  }, []);

  const formatDate = (isoString) => {
    const date = new Date(isoString);
    return date.toLocaleDateString('fr-FR', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  const getRandomRating = () => {
    // Generate rating between 4 and 5 stars for display
    return Math.random() > 0.3 ? 5 : 4;
  };

  return (
    <div className="min-h-screen pt-6 md:pt-20 pb-12 md:pb-16 px-4 md:px-6">
      {/* Header */}
      <div className="text-center mb-8 md:mb-12 animate-fadeInUp">
        <span className="inline-block px-3 py-1 rounded-full bg-cyan-950/50 border border-cyan-800/50 text-cyan-400 text-[10px] md:text-xs font-semibold uppercase tracking-wider mb-3 md:mb-4">
          {t('feedbacks.badge')}
        </span>
        <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-2 md:mb-3 px-2">
          {t('feedbacks.title')} <span className="text-cyan-500">{t('feedbacks.titleHighlight')}</span>
        </h1>
        <p className="text-gray-400 max-w-xl mx-auto text-xs md:text-sm px-4">
          {t('feedbacks.subtitle')}
        </p>
      </div>

      {/* Stats */}
      <div className="max-w-4xl mx-auto mb-6 md:mb-10">
        <div className="grid grid-cols-3 gap-2 md:gap-4">
          <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-2.5 md:p-4 text-center">
            <div className="text-xl md:text-3xl font-bold text-cyan-500">+{stats.reviewsCount}</div>
            <div className="text-[9px] md:text-xs text-gray-400">{t('feedbacks.verifiedReviews')}</div>
            <div className="text-[8px] md:text-[10px] text-gray-500 mt-1">{t('feedbacks.onDiscord')}</div>
          </div>
          <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-2.5 md:p-4 text-center">
            <div className="text-xl md:text-3xl font-bold text-green-500 flex items-center justify-center gap-0.5 md:gap-1">
              {stats.rating} <Star className="w-3 h-3 md:w-5 md:h-5 fill-current" />
            </div>
            <div className="text-[9px] md:text-xs text-gray-400">{t('feedbacks.averageRating')}</div>
          </div>
          <div className="bg-zinc-900/50 border border-cyan-900/30 rounded-xl p-2.5 md:p-4 text-center">
            <div className="text-xl md:text-3xl font-bold text-blue-500">100%</div>
            <div className="text-[9px] md:text-xs text-gray-400">{t('feedbacks.satisfaction')}</div>
          </div>
        </div>
      </div>

      {/* Refresh Button */}
      <div className="max-w-4xl mx-auto mb-4 md:mb-6 flex justify-end">
        <button
          onClick={fetchReviews}
          disabled={loading}
          className="flex items-center gap-1.5 md:gap-2 px-3 md:px-4 py-1.5 md:py-2 bg-zinc-800 hover:bg-zinc-700 text-gray-300 rounded-lg transition-colors text-xs md:text-sm disabled:opacity-50"
          data-testid="refresh-reviews-btn"
        >
          <RefreshCw className={`w-3 h-3 md:w-4 md:h-4 ${loading ? 'animate-spin' : ''}`} />
          {t('feedbacks.refresh')}
        </button>
      </div>

      {/* Reviews Grid */}
      <div className="max-w-4xl mx-auto">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-12 md:py-16">
            <div className="w-8 h-8 md:w-10 md:h-10 border-2 border-cyan-500 border-t-transparent rounded-full animate-spin mb-3 md:mb-4"></div>
            <p className="text-gray-400 text-sm">{t('feedbacks.loading')}</p>
          </div>
        ) : error ? (
          <div className="text-center py-12 md:py-16">
            <MessageSquare className="w-12 h-12 md:w-16 md:h-16 text-gray-600 mx-auto mb-3 md:mb-4" />
            <p className="text-gray-400 text-sm md:text-base">{error}</p>
            <button
              onClick={fetchReviews}
              className="mt-3 md:mt-4 px-4 md:px-6 py-2 bg-cyan-600 hover:bg-cyan-700 text-white rounded-lg transition-colors text-sm"
            >
              {t('feedbacks.retry')}
            </button>
          </div>
        ) : reviews.length === 0 ? (
          <div className="text-center py-12 md:py-16">
            <svg className="w-12 h-12 md:w-16 md:h-16 text-[#5865F2] mx-auto mb-4" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            <p className="text-white font-semibold text-lg mb-2">{t('feedbacks.reviewsOnDiscord')}</p>
            <p className="text-gray-400 text-sm mb-4">{t('feedbacks.reviewsOnDiscordDesc')}</p>
            <a
              href="https://discord.gg/aqmopti"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-5 py-2.5 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
              </svg>
              {t('feedbacks.viewOnDiscord')}
            </a>
          </div>
        ) : (
          <div className="grid gap-3 md:gap-4" data-testid="reviews-grid">
            {reviews.map((review, index) => {
              const rating = getRandomRating();
              return (
                <div
                  key={index}
                  className="bg-gradient-to-br from-zinc-900/80 to-zinc-900/40 border border-cyan-900/30 rounded-xl p-3 md:p-5 hover:border-cyan-500/50 transition-all duration-300"
                  data-testid={`review-card-${index}`}
                >
                  <div className="flex items-start gap-3 md:gap-4">
                    {/* Avatar */}
                    <div className="flex-shrink-0">
                      {review.avatar ? (
                        <img
                          src={review.avatar}
                          alt={review.author}
                          className="w-10 h-10 md:w-12 md:h-12 rounded-full border-2 border-cyan-500/50"
                        />
                      ) : (
                        <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-cyan-600/30 flex items-center justify-center border-2 border-cyan-500/50">
                          <User className="w-5 h-5 md:w-6 md:h-6 text-cyan-400" />
                        </div>
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-1.5 md:mb-2 gap-1">
                        <div>
                          <h3 className="font-semibold text-white text-sm md:text-base">{review.author}</h3>
                          <div className="flex items-center gap-0.5 mt-0.5 md:mt-1">
                            {[...Array(5)].map((_, i) => (
                              <Star
                                key={i}
                                className={`w-3 h-3 md:w-4 md:h-4 ${
                                  i < rating
                                    ? 'text-yellow-500 fill-current'
                                    : 'text-gray-600'
                                }`}
                              />
                            ))}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 text-[10px] md:text-xs text-gray-500">
                          <Clock className="w-2.5 h-2.5 md:w-3 md:h-3" />
                          {formatDate(review.timestamp)}
                        </div>
                      </div>
                      
                      <p 
                        className="text-gray-300 text-xs md:text-sm leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: parseDiscordContent(review.content) }}
                      />

                      {/* Verified Badge */}
                      <div className="mt-2 md:mt-3 flex flex-wrap items-center gap-1.5 md:gap-2">
                        <span className="inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 bg-green-500/20 text-green-400 text-[10px] md:text-xs rounded-full">
                          <svg className="w-2.5 h-2.5 md:w-3 md:h-3" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                          </svg>
                          {t('feedbacks.verified')}
                        </span>
                        <span className="inline-flex items-center gap-1 px-1.5 md:px-2 py-0.5 bg-blue-500/20 text-blue-400 text-[10px] md:text-xs rounded-full">
                          <svg className="w-2.5 h-2.5 md:w-3 md:h-3" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515a.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0a12.64 12.64 0 0 0-.617-1.25a.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057a19.9 19.9 0 0 0 5.993 3.03a.078.078 0 0 0 .084-.028a14.09 14.09 0 0 0 1.226-1.994a.076.076 0 0 0-.041-.106a13.107 13.107 0 0 1-1.872-.892a.077.077 0 0 1-.008-.128a10.2 10.2 0 0 0 .372-.292a.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127a12.299 12.299 0 0 1-1.873.892a.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028a19.839 19.839 0 0 0 6.002-3.03a.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03z"/>
                          </svg>
                          Discord
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Call to Action */}
      <div className="max-w-4xl mx-auto mt-8 md:mt-12 text-center">
        <div className="bg-gradient-to-br from-cyan-950/30 to-zinc-900/50 border border-cyan-500/30 rounded-xl p-5 md:p-8">
          <h2 className="text-lg md:text-2xl font-bold text-white mb-1.5 md:mb-2">{t('feedbacks.leaveReview')}</h2>
          <p className="text-gray-400 mb-4 md:mb-6 text-xs md:text-base">
            {t('feedbacks.shareExperience')}
          </p>
          <a
            href="https://discord.gg/aqmopti"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-2 px-4 md:px-6 py-2 md:py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white font-semibold rounded-lg transition-all duration-300 hover:scale-105 text-sm md:text-base"
            data-testid="join-discord-btn"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20.317 4.37a19.791 19.791 0 0 0-4.885-1.515.074.074 0 0 0-.079.037c-.21.375-.444.864-.608 1.25a18.27 18.27 0 0 0-5.487 0 12.64 12.64 0 0 0-.617-1.25.077.077 0 0 0-.079-.037A19.736 19.736 0 0 0 3.677 4.37a.07.07 0 0 0-.032.027C.533 9.046-.32 13.58.099 18.057a.082.082 0 0 0 .031.057 19.9 19.9 0 0 0 5.993 3.03.078.078 0 0 0 .084-.028c.462-.63.874-1.295 1.226-1.994a.076.076 0 0 0-.041-.106 13.107 13.107 0 0 1-1.872-.892.077.077 0 0 1-.008-.128 10.2 10.2 0 0 0 .372-.292.074.074 0 0 1 .077-.01c3.928 1.793 8.18 1.793 12.062 0a.074.074 0 0 1 .078.01c.12.098.246.198.373.292a.077.077 0 0 1-.006.127 12.299 12.299 0 0 1-1.873.892.077.077 0 0 0-.041.107c.36.698.772 1.362 1.225 1.993a.076.076 0 0 0 .084.028 19.839 19.839 0 0 0 6.002-3.03.077.077 0 0 0 .032-.054c.5-5.177-.838-9.674-3.549-13.66a.061.061 0 0 0-.031-.03zM8.02 15.33c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.956-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.956 2.418-2.157 2.418zm7.975 0c-1.183 0-2.157-1.085-2.157-2.419 0-1.333.955-2.419 2.157-2.419 1.21 0 2.176 1.096 2.157 2.42 0 1.333-.946 2.418-2.157 2.418z"/>
            </svg>
            {t('feedbacks.joinDiscord')}
          </a>
        </div>
      </div>
    </div>
  );
};

export default FeedbacksPage;
